import { useCallback } from 'react';
import { Id } from 'react-toastify';
import { EventName } from 'services/eventTrackingServiceTypes';
import { useEventsContext } from 'context/events/EventsContext';
import getElapsedTimeMs from '../../util/getElapsedTimeMs';
import {
  AudioFileFormat,
  ScriptFormat,
} from '../../../server/graphql/__generated.types';
import { useDownloadTakesMutation } from './operation.generated';
import { toast, updateToast } from '../Toaster';
import performDownload from './performDownload';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import handleEntitlementDownloadError, {
  getGraphQLErrorCode,
} from './handleEntitlementDownloadError';

const useDownloadTakes = () => {
  const [, dispatchPrompt] = usePromptsContext();
  const [download] = useDownloadTakesMutation();
  const [eventTracking] = useEventsContext();

  const downloadTakes = useCallback(
    async (inputs: {
      takeIds: string[];
      audioFileFormat: AudioFileFormat;
      includeScriptFormats?: Array<ScriptFormat>;
      setDownloadError?: (downloadError: string) => void;
      source: 'section' | 'modal' | 'bulk';
      projectId: string;
      filename?: string | null;
      includeProjectName?: boolean;
      numberFilesAutomatically?: boolean;
      msGapDuration?: number;
      onCombineDownloadError?: () => void;
      showTimeoutPrompt?: boolean;
    }) => {
      const {
        takeIds,
        audioFileFormat,
        includeScriptFormats,
        setDownloadError,
        source,
        projectId,
        filename,
        includeProjectName,
        numberFilesAutomatically,
        msGapDuration,
        onCombineDownloadError,
        showTimeoutPrompt,
      } = inputs;
      const startTime = performance.now();

      let toastId: Id | undefined;

      const action = msGapDuration ? 'Combining' : 'Downloading';
      const sectionsPlural = takeIds.length > 1 ? 'sections' : 'section';
      const errorMessage = `Failed to ${
        msGapDuration ? 'combined' : 'download'
      } ${sectionsPlural}`;

      try {
        toastId = toast({
          message: `${action} ${takeIds.length} ${sectionsPlural}`,
          type: 'loading',
        });

        await download({
          variables: {
            inputs: {
              audioFileFormat,
              takeIds,
              includeScriptFormats,
              projectId,
              filename,
              includeProjectName,
              numberFilesAutomatically,
              msGapDuration,
            },
          },
          update: (_proxy, { data }) => {
            const integrationType =
              data?.downloadTakes?.integrationType ?? undefined;

            if (!data?.downloadTakes.success) {
              const errorMsg = data?.downloadTakes.message ?? errorMessage;
              const combineErrorCode = data?.downloadTakes.code;

              const shouldShowTimeoutPrompt =
                showTimeoutPrompt &&
                combineErrorCode === 'network_timeout_error' &&
                typeof onCombineDownloadError === 'function';

              handleEntitlementDownloadError({
                errorCode: combineErrorCode,
                toastId,
                errorMessage: errorMsg,
                dispatchPrompt,
                onTimeout: shouldShowTimeoutPrompt
                  ? onCombineDownloadError
                  : undefined,
              });

              if (
                !shouldShowTimeoutPrompt &&
                data?.downloadTakes.code === 'take_download_limit_exceeded' &&
                setDownloadError
              ) {
                setDownloadError(errorMsg);
              }

              eventTracking.track({
                type: EventName.TakeDownload,
                data: {
                  source,
                  takeIds,
                  audioFormat: audioFileFormat,
                  success: false,
                  elapsedTimeMs: getElapsedTimeMs(startTime),
                  takesCount: takeIds.length,
                  error: errorMsg,
                  includeScriptFormats: includeScriptFormats ?? [],
                  integrationType,
                },
              });
            } else {
              const { data: downloadData } = data.downloadTakes;

              // Combining takes, track event
              if (msGapDuration) {
                eventTracking.track({
                  type: EventName.TakeCombine,
                  data: {
                    count: takeIds.length,
                    msGapDuration,
                    filename: filename || '',
                  },
                });
              }

              eventTracking.track({
                type: EventName.TakeDownload,
                data: {
                  source,
                  takeIds,
                  audioFormat: audioFileFormat,
                  success: true,
                  elapsedTimeMs: getElapsedTimeMs(startTime),
                  takesCount: takeIds.length,
                  combinedTextLength:
                    downloadData?.downloadMetricsMetadata.combinedTextLength ??
                    0,
                  numberOfTakesJustConverted:
                    downloadData?.downloadMetricsMetadata
                      .numberOfClipsJustConverted ?? 0,
                  zipFileAlreadyExisted:
                    downloadData?.downloadMetricsMetadata
                      .zipFileAlreadyExisted ?? false,
                  includeScriptFormats: includeScriptFormats ?? [],
                  integrationType,
                },
              });

              performDownload({
                fileFormat: downloadData!.fileFormat,
                friendlyFileName: downloadData!.friendlyFileName,
                signedURL: downloadData!.signedURL,
              });

              updateToast(toastId ?? '', {
                message: 'Successfully downloaded',
                type: 'success',
              });
            }
          },
        });
      } catch (error) {
        const combineErrorCode = getGraphQLErrorCode(error);
        const shouldShowTimeoutPrompt =
          showTimeoutPrompt &&
          typeof onCombineDownloadError === 'function' &&
          combineErrorCode === 'network_timeout_error';

        handleEntitlementDownloadError({
          errorCode: combineErrorCode,
          toastId,
          errorMessage,
          dispatchPrompt,
          onTimeout: shouldShowTimeoutPrompt
            ? onCombineDownloadError
            : undefined,
        });
      }
    },
    [dispatchPrompt, download, eventTracking]
  );

  return downloadTakes;
};

export default useDownloadTakes;
